import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Bell, AlertCircle, Activity, Loader2 } from "lucide-react";

const TYPE_META = {
  high_dil:  { icon: Activity,     color: "rose" },
  no_data:   { icon: AlertCircle,  color: "amber" },
  worsening: { icon: AlertCircle,  color: "rose" },
};

export default function DoctorAlertsPanel({ onSelectPatient }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("nutramilo_access_token") || sessionStorage.getItem("nutramilo_access_token");
        const { data } = await axios.get(`${API}/doctor/alerts`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setAlerts(data.alerts || []);
      } catch { setAlerts([]); }
      finally { setLoading(false); }
    })();
  }, [API]);

  const cardCls = `rounded-xl border ${isDark ? "bg-[hsl(223,47%,10%)] border-white/5" : "bg-white border-gray-200"}`;
  const top = alerts.slice(0, 5);

  return (
    <div className={cardCls} data-testid="alerts-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-white/5">
        <div className="flex items-center gap-2">
          <Bell className={`w-4 h-4 ${isDark ? "text-rose-400" : "text-rose-500"}`} />
          <h3 className="text-sm font-bold">{bg ? "Известия" : "Alerts"}</h3>
          {alerts.length > 0 && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-rose-500 text-white" data-testid="alert-count">
              {alerts.length}
            </span>
          )}
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
        ) : top.length === 0 ? (
          <p className={`text-xs text-center py-6 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
            {bg ? "Няма известия" : "No alerts"}
          </p>
        ) : top.map((a, i) => {
          const meta = TYPE_META[a.type] || TYPE_META.no_data;
          const Icon = meta.icon;
          const colorCls = meta.color === "rose"
            ? (isDark ? "bg-rose-500/10 text-rose-300" : "bg-rose-50 text-rose-600")
            : (isDark ? "bg-amber-500/10 text-amber-300" : "bg-amber-50 text-amber-600");
          return (
            <button
              key={i}
              onClick={() => onSelectPatient?.(a.patient_id)}
              className={`w-full text-left px-4 py-2.5 border-b border-gray-100 dark:border-white/5
                transition-colors ${isDark ? "hover:bg-white/[0.02]" : "hover:bg-gray-50"}`}
              data-testid={`alert-item-${i}`}
            >
              <div className="flex items-start gap-2">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${colorCls}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate">{bg ? a.label_bg : a.label_en}</p>
                  <p className={`text-[10px] truncate ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    {a.patient_name}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
